import { doc, getDoc } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

/**
 * The organiser's after-the-event summary, at events/{e}/post_event/summary
 * (TNG-96). Written only by the SAVE_AFTER_EVENT_SUMMARY trigger, which
 * sanitises the HTML first, so it is rendered as stored.
 */
export const afterEventSummarySchema = z.object({
  html: z.string(),
  updatedAt: z.any().optional(),
  updatedBy: z.string().optional(),
});
export type AfterEventSummary = z.infer<typeof afterEventSummarySchema>;

export type SummaryState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; summary: AfterEventSummary | null };

export function summaryRef(eventId: string) {
  return doc(db, "events", eventId, "post_event", "summary");
}

export function useAfterEventSummary(eventId: string): SummaryState & {
  reload: () => void;
} {
  const [state, setState] = useState<SummaryState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getDoc(summaryRef(eventId))
      .then((snapshot) => {
        if (cancelled) return;
        if (!snapshot.exists()) {
          setState({ status: "success", summary: null });
          return;
        }
        const parsed = afterEventSummarySchema.safeParse(snapshot.data());
        setState(
          parsed.success
            ? { status: "success", summary: parsed.data }
            : { status: "error", error: "The stored summary is unreadable" },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Whether rich text shows anything. The editor saves a cleared field as
 * "<p></p>", which would otherwise render an empty card.
 */
export function hasVisibleHtml(html: string | undefined | null): boolean {
  if (!html) return false;
  if (/<hr\b/i.test(html)) return true;
  return (
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length > 0
  );
}
